import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';

type LiveBackground = {
  BackgroundID: string;
  Name: string;
  FileKey: string;
  FileUrl: string;
  UploadedBy: string;
  IsActive: boolean;
  createdAt?: string;
  updatedAt?: string;
};

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

const LiveBackgrounds: React.FC = () => {
  const navigate = useNavigate();
  const [backgrounds, setBackgrounds] = useState<LiveBackground[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const fetchBackgrounds = useCallback(
    async (pageNum: number) => {
      const headers = headerWithAuth();
      if (!headers) return;

      setLoading(true);
      try {
        const res = await fetch(
          `${liveApiBase}/backgrounds?page=${pageNum}&limit=${limit}&activeOnly=false`,
          { headers }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to load backgrounds');
        const data = json.data as { backgrounds: LiveBackground[]; total: number; hasMore: boolean };
        setBackgrounds(data.backgrounds);
        setTotal(data.total);
        setHasMore(data.hasMore);
      } catch (error) {
        toast.error((error as Error).message || 'Failed to fetch backgrounds');
      } finally {
        setLoading(false);
      }
    },
    [headerWithAuth]
  );

  useEffect(() => {
    fetchBackgrounds(page);
  }, [fetchBackgrounds, page]);

  const handleDelete = useCallback(
    async (backgroundId: string) => {
      const headers = headerWithAuth();
      if (!headers) return;

      setDeletingId(backgroundId);
      try {
        const res = await fetch(`${liveApiBase}/backgrounds/${encodeURIComponent(backgroundId)}`, {
          method: 'DELETE',
          headers,
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to delete background');
        toast.success(json.message || 'Background deleted');
        setBackgrounds((prev) => prev.filter((b) => b.BackgroundID !== backgroundId));
        setTotal((prev) => prev - 1);
      } catch (error) {
        toast.error((error as Error).message || 'Failed to delete background');
      } finally {
        setDeletingId('');
      }
    },
    [headerWithAuth]
  );

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Live Backgrounds</h1>
            <p className='text-sm text-gray-400'>JPEG backgrounds available for live streams.</p>
          </div>
          <div className='flex items-center gap-3'>
            <div className='text-sm text-gray-400'>
              {loading ? 'Loading...' : `${total} background${total !== 1 ? 's' : ''}`}
            </div>
            <button
              onClick={() => navigate('/live-backgrounds/new')}
              className='px-4 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'
            >
              Upload Background
            </button>
          </div>
        </div>

        {loading ? (
          <div className='text-sm text-gray-400'>Fetching backgrounds...</div>
        ) : backgrounds.length === 0 ? (
          <div className='text-sm text-gray-400'>No backgrounds found.</div>
        ) : (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
              {backgrounds.map((bg) => (
                <div
                  key={bg.BackgroundID}
                  className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-lg'
                >
                  <div className='h-48 bg-white/10 flex items-center justify-center overflow-hidden'>
                    <img
                      src={bg.FileUrl}
                      alt={bg.Name}
                      className='w-full h-full object-cover'
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                  <div className='p-4 space-y-2'>
                    <div className='text-sm font-semibold text-white'>{bg.Name}</div>
                    <div className='text-xs text-gray-400 truncate'>ID: {bg.BackgroundID}</div>
                    <div className='flex items-center gap-2 text-xs'>
                      <span
                        className={`px-2 py-0.5 rounded-full border ${
                          bg.IsActive
                            ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                            : 'text-red-300 border-red-400/40 bg-red-500/10'
                        }`}
                      >
                        {bg.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <a
                      href={bg.FileUrl}
                      target='_blank'
                      rel='noreferrer'
                      className='text-xs text-[#FF9C15] hover:underline break-all block'
                    >
                      {bg.FileKey}
                    </a>
                    {bg.createdAt && (
                      <div className='text-xs text-gray-500'>
                        {new Date(bg.createdAt).toLocaleDateString()}
                      </div>
                    )}
                    <button
                      onClick={() => handleDelete(bg.BackgroundID)}
                      disabled={deletingId === bg.BackgroundID}
                      className='mt-2 w-full px-3 py-2 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-60'
                    >
                      {deletingId === bg.BackgroundID ? 'Deleting...' : 'Delete Background'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className='flex items-center justify-between mt-8'>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
              >
                Previous
              </button>
              <span className='text-xs text-gray-400'>Page {page}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore || loading}
                className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LiveBackgrounds;
